/**
 * Socket.io Client Service
 * Handles real-time WebSocket connections for notifications, messaging, etc.
 */

import { io } from 'socket.io-client';
import { API_URL } from '../apiConfig';
import { supabase } from '../config/supabase';

class SocketClient {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.connectionAttempts = 0;
    this.maxConnectionAttempts = 3;
    this.lastConnectionAttempt = 0;
    this.connectionCooldown = 10000; // 10 seconds
  }

  /**
   * Connect to Socket.io server
   */
  async connect() {
    if (this.socket?.connected) {
      return;
    }

    // Rate limit connection attempts
    const now = Date.now();
    if (this.connectionAttempts >= this.maxConnectionAttempts) {
      if (now - this.lastConnectionAttempt < this.connectionCooldown) {
        // Still in cooldown period, skip connection attempt
        return;
      } else {
        // Reset attempts after cooldown
        this.connectionAttempts = 0;
      }
    }

    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        // Silently skip connection if no auth token (user not logged in)
        return;
      }

      this.connectionAttempts++;
      this.lastConnectionAttempt = now;

      // Get View As context
      const viewAsContext = this.getViewAsContext();

      // Create socket connection
      this.socket = io(API_URL, {
        auth: {
          token: session.access_token
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });

      // Add View As header if in view mode
      if (viewAsContext?.userId) {
        this.socket.io.opts.extraHeaders = {
          'X-View-As-User-ID': viewAsContext.userId
        };
      }

      // Connection event handlers
      this.socket.on('connect', () => {
        this.isConnected = true;
        this.connectionAttempts = 0; // Reset on successful connection
        if (import.meta.env.DEV) {
          console.log('Socket.io connected');
        }
        this.subscribeToNotifications();
      });
      
      // Handle authentication errors - refresh token and reconnect
      this.socket.on('disconnect', (reason) => {
        this.isConnected = false;
        // If disconnected due to authentication, try to refresh token
        if (reason === 'io server disconnect' || reason === 'unauthorized') {
          this.refreshTokenAndReconnect();
        }
        // Only log unexpected disconnects in dev mode
        if (import.meta.env.DEV && reason !== 'io server disconnect') {
          console.log('Socket.io disconnected:', reason);
        }
      });

      // Suppress connection error logs (expected when backend is down)
      this.socket.on('connect_error', () => {
        this.isConnected = false;
        // Silently handle connection errors - don't spam console
        // The socket will automatically retry
        // Only log once per connection attempt in dev mode
        if (import.meta.env.DEV && this.connectionAttempts === 1) {
          console.log('Socket.io connection attempt (backend may be offline)');
        }
      });


      // Message event handlers
      this.socket.on('message:new', (data) => {
        this.emit('message:new', data);
      });

      // Appointment event handlers
      this.socket.on('appointment:updated', (data) => {
        this.emit('appointment:updated', data);
      });

      // Notification event handlers
      this.socket.on('notification:new', (data) => {
        this.emit('notification:new', data);
      });

      // Typing indicators
      this.socket.on('typing:start', (data) => {
        this.emit('typing:start', data);
      });

      this.socket.on('typing:stop', (data) => {
        this.emit('typing:stop', data);
      });

    } catch (error) {
      // Only log unexpected errors (connection failures are expected when server is down)
      if (import.meta.env.DEV && error.message && !error.message.includes('connection')) {
        console.warn('Socket.io setup error:', error.message);
      }
    }
  }

  /**
   * Refresh token and reconnect
   */
  async refreshTokenAndReconnect() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: { session: newSession }, error } = await supabase.auth.refreshSession(session);
        if (!error && newSession) {
          // Disconnect old socket
          if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
          }
          // Reconnect with new token
          await this.connect();
        }
      }
    } catch (err) {
      console.warn('Token refresh failed:', err);
    }
  }

  /**
   * Disconnect from Socket.io server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * Subscribe to notifications
   */
  subscribeToNotifications() {
    if (this.socket?.connected) {
      this.socket.emit('notifications:subscribe');
    }
  }

  /**
   * Unsubscribe from notifications
   */
  unsubscribeFromNotifications() {
    if (this.socket?.connected) {
      this.socket.emit('notifications:unsubscribe');
    }
  }

  /**
   * Send a message via Socket.io
   */
  sendMessage(messageData) {
    if (this.socket?.connected) {
      this.socket.emit('message:send', messageData);
    } else {
      console.warn('Socket.io not connected. Message not sent.');
    }
  }

  /**
   * Emit appointment update
   */
  emitAppointmentUpdate(updateData) {
    if (this.socket?.connected) {
      this.socket.emit('appointment:update', updateData);
    }
  }

  /**
   * Start typing indicator
   */
  startTyping(recipientId, conversationId) {
    if (this.socket?.connected) {
      this.socket.emit('typing:start', { recipientId, conversationId });
    }
  }

  /**
   * Stop typing indicator
   */
  stopTyping(recipientId, conversationId) {
    if (this.socket?.connected) {
      this.socket.emit('typing:stop', { recipientId, conversationId });
    }
  }

  /**
   * Add event listener
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to local listeners
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Get View As context from session storage
   */
  getViewAsContext() {
    try {
      const saved = sessionStorage.getItem('viewMode');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.active) {
          return {
            userId: parsed.userId,
            tenantId: parsed.tenantId
          };
        }
      }
    } catch (error) {
      // Ignore errors
    }
    return null;
  }
}

// Export singleton instance
const socketClient = new SocketClient();
export default socketClient;

